import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SEARCH_RESULT_TYPES, type GlobalSearchResponse, type SearchResult, type SearchResultType } from '@fint/validation';
import { api } from '@/lib/api';

/**
 * Everything the search does apart from how it looks - ported from Desk's
 * `useGlobalSearch` so the two products answer a search the same way: 200ms
 * of quiet before asking, two characters before anything is asked, results
 * grouped by kind in a fixed order, and one arrow-key walk across the groups.
 */
export function useGlobalSearch(orgId: string | null | undefined, options: { limit?: number; initialQ?: string } = {}) {
  const [q, setQ] = useState(options.initialQ ?? '');
  const [debounced, setDebounced] = useState((options.initialQ ?? '').trim());
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', orgId, debounced, options.limit ?? null],
    queryFn: () =>
      api.get<GlobalSearchResponse>(`/search?q=${encodeURIComponent(debounced)}${options.limit ? `&limit=${options.limit}` : ''}`),
    enabled: !!orgId && debounced.length >= 2,
    staleTime: 30_000,
    // Keep the last answer on screen while the next one is fetched, rather
    // than flashing "Searching…" between every keystroke.
    placeholderData: (previous) => previous,
  });
  const results = useMemo(() => data?.results ?? [], [data]);

  useEffect(() => setHighlight(0), [results.length]);

  const groups = useMemo(() => {
    const out = Object.fromEntries(SEARCH_RESULT_TYPES.map((t) => [t, [] as SearchResult[]])) as Record<SearchResultType, SearchResult[]>;
    for (const r of results) out[r.type]?.push(r);
    return out;
  }, [results]);
  const flat = useMemo(() => SEARCH_RESULT_TYPES.flatMap((t) => groups[t]), [groups]);
  /** More than one book in the answer: each row then says which book it is in. */
  const manyBooks = (data?.books.length ?? 0) > 1;

  const reset = () => {
    setQ('');
    setDebounced('');
    setHighlight(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, onPick: (r: SearchResult) => void, onEscape: () => void) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onEscape();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(flat.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = flat[Math.min(highlight, flat.length - 1)];
      if (target) onPick(target);
    }
  };

  return { q, setQ, debounced, data, isFetching, groups, flat, manyBooks, highlight, setHighlight, reset, handleKeyDown };
}
